/**
 * Meta ad post-creation editor.
 *
 * Inline card editor for existing Meta ads — the analog of Google's
 * RSAEditor / RDAEditor. Click on an ad row in
 * `MetaManagementAccordion.AdsTab` swaps the accordion's content for
 * this editor (same shape as `AssetManagement/index.tsx:222` renders
 * `<RSAEditor>` inline when `editingRSA` is set). On save, if the
 * creative copy / image changed the backend archive-and-recreates the
 * creative (Meta doesn't allow in-place `adcreative` PATCH); name-only
 * changes are the fast path.
 *
 * **Status changes are NOT surfaced here.** Pause / enable / archive
 * live on the Campaign Structure kebab (`MetaCampaignElementsList`) —
 * single source of truth for status writes across the Meta surface.
 * This editor scopes to name + creative copy + image edits.
 *
 * Creative-kind branches:
 *   - `image` (link ads): renders the same `LinkCreativeStep` the wizard
 *     uses so field-level UX (char counts, image picker, lead-form
 *     dropdown, Advantage+ toggle) is bit-for-bit identical to
 *     creation. **Deliberate reuse** — mirrors the memory
 *     `feedback-dry-generalize-working-code`. Do NOT copy the fields.
 *   - `video` / `carousel` / `unknown`: renders a read-only preview
 *     with an "edit in Meta Ads Manager" notice. Ad name is still
 *     editable (backend allows it regardless of creative type).
 *
 * Error handling:
 *   - 409 (`MetaAuthException` mapped): surfaces the standard
 *     `MetaTokenExpiringBanner` re-auth CTA via a toast + inline error.
 *     Do NOT surface as 401 — that would trip the frontend logout
 *     interceptor. See CLAUDE.md theme #22a.
 *   - Other errors: inline red banner + toast; user can retry or cancel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CloseIcon } from '@/components/ui/Icons';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useMaterializePendingTargeted } from '@/hooks/useMaterializePendingTargeted';
import {
  metaAdsApi,
  type GetMetaAdResponse,
  type MetaAdvantagePlusCreativeSpec,
  type MetaLinkCreativeSpec,
  type UpdateMetaAdRequest,
} from '@/services/api/metaAds';
import { useAuthStore } from '@/store/authStore';
import type { AppConfiguration } from '@/types/api';
import type { PendingTargeted } from '@/types/media';
import { applyMetaAdRename } from '@/utils/optimisticMetaCache';
import { toast, debug } from '@/utils';

import { LinkCreativeStep } from './steps/ad/LinkCreativeStep';

export interface MetaAdEditorProps {
  /** `act_<digits>` — passed to `getAd` / `updateAd`. */
  adAccountId: string;
  /** Numeric Meta ad ID. */
  adId: string;
  /** Ad set name for the editor header subtitle — display only. */
  adSetName?: string | null;
  /** Called after a successful save so the parent can invalidate
   *  React Query caches (ad list, app_configuration snapshot). */
  onSaved: () => void;
  /** Called when the user cancels or closes the editor — parent
   *  clears its `editingAd` state so the ads-list tab panel comes
   *  back into view. */
  onClose: () => void;
}

const AD_NAME_MAX = 400;

const defaultAdvantagePlus = (): MetaAdvantagePlusCreativeSpec => ({
  enabled: false,
  text_variations: true,
  image_variations: true,
});

/** Map a 409 axios error into a user-visible re-auth message. Copes
 *  with both `axios.AxiosError` shape and the fetch-style Response
 *  fallback our `apiClient` occasionally throws. */
function formatSaveError(err: unknown): string {
  // AxiosError-shaped
  const anyErr = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
  const status = anyErr?.response?.status;
  const detail = anyErr?.response?.data?.detail;
  if (status === 409) {
    return (
      'Meta ad account authorization has expired. Please reconnect ' +
      'your Meta account and try again.'
    );
  }
  if (status === 422 && detail) return detail;
  if (status === 400 && detail) return detail;
  if (status === 504) return 'Meta request timed out. Try saving again.';
  if (detail) return detail;
  return anyErr?.message ?? 'Failed to save ad. Try again.';
}

export function MetaAdEditor({
  adAccountId,
  adId,
  adSetName,
  onSaved,
  onClose,
}: MetaAdEditorProps) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ad, setAd] = useState<GetMetaAdResponse | null>(null);

  // ── Editable state ─────────────────────────────────────────────────
  const [adName, setAdName] = useState<string>('');
  const [linkSpec, setLinkSpec] = useState<MetaLinkCreativeSpec | null>(null);
  const [pendingImage, setPendingImage] = useState<PendingTargeted | null>(null);
  const [advantagePlusCreative, setAdvantagePlusCreative] =
    useState<MetaAdvantagePlusCreativeSpec>(defaultAdvantagePlus);

  // ── UX ──────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const materializePending = useMaterializePendingTargeted();
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((s) => s.user?.memberUuid);

  // ── Load ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const response = await metaAdsApi.getAd(adAccountId, adId);
        if (cancelled) return;
        setAd(response);
        setAdName(response.ad_name);
        if (response.creative_spec) {
          setLinkSpec(response.creative_spec);
        }
        // Advantage+ isn't returned on GET (Meta doesn't surface
        // degrees_of_freedom_spec on the read side). Default to
        // Standard — user can opt in on save if they want variants.
      } catch (err) {
        if (cancelled) return;
        debug.log('MetaAdEditor: load failed', err);
        const anyErr = err as { response?: { status?: number } };
        if (anyErr?.response?.status === 409) {
          setLoadError(
            'Meta ad account authorization has expired. Please reconnect ' +
            'your Meta account.',
          );
        } else {
          setLoadError('Failed to load ad. Close and try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adAccountId, adId]);

  // ── Dirty detection ────────────────────────────────────────────────
  const nameDirty = ad != null && adName !== ad.ad_name;
  const linkSpecDirty = useMemo(() => {
    if (!ad?.creative_spec || !linkSpec) return false;
    const orig = ad.creative_spec;
    return (
      linkSpec.headline !== orig.headline ||
      linkSpec.primary_text !== orig.primary_text ||
      (linkSpec.description ?? '') !== (orig.description ?? '') ||
      linkSpec.link_url !== orig.link_url ||
      (linkSpec.call_to_action ?? 'LEARN_MORE') !== (orig.call_to_action ?? 'LEARN_MORE') ||
      (linkSpec.lead_gen_form_id ?? '') !== (orig.lead_gen_form_id ?? '')
    );
  }, [ad?.creative_spec, linkSpec]);
  const imageDirty = pendingImage != null;
  const creativeDirty = linkSpecDirty || imageDirty;
  const anyDirty = nameDirty || creativeDirty;

  // ── Save ────────────────────────────────────────────────────────────
  const performSave = async () => {
    if (!ad) return;
    setSaving(true);
    setSaveError(null);
    try {
      let payload: UpdateMetaAdRequest = {};
      if (nameDirty) payload.ad_name = adName.trim();

      if (creativeDirty && linkSpec) {
        // Materialize the pending image (if any) → get a signed GCS URL
        // the backend fetches at Meta creative-create time. When the
        // user didn't swap the image, we reuse the current linkSpec's
        // image_url — which came from the current creative on load.
        let effectiveImageUrl = linkSpec.image_url;
        if (pendingImage) {
          const materialized = await materializePending([pendingImage]);
          if (materialized.length === 0) {
            setSaveError('Image upload failed. Try picking again.');
            setSaving(false);
            return;
          }
          effectiveImageUrl = materialized[0].signed_url;
        }
        payload = {
          ...payload,
          creative: {
            mode: 'new',
            spec: { ...linkSpec, image_url: effectiveImageUrl },
          },
          advantage_plus_creative: advantagePlusCreative,
        };
      }

      const response = await metaAdsApi.updateAd(adAccountId, adId, payload);
      debug.log('MetaAdEditor: save succeeded', {
        ad_id: response.ad_id,
        new_creative_id: response.new_creative_id,
      });
      // Instant-feedback splice into the app-config cache before the
      // React Query invalidation triggers a refetch. Without this, the
      // AdsTab keeps showing the old ad_name / status until the network
      // round-trip completes (~1s+). Splices match `applied_entity`
      // patterns used by CreateMetaAdWizard.
      // `useCampaignFromCache` derives from app-config, so the splice
      // flows through to the ad-set/ad rows automatically.
      if (memberUuid && nameDirty) {
        // `response.ad_name` is populated because we sent one; fall
        // back to the local dirty value for TS narrowing.
        const nextName = response.ad_name ?? adName.trim();
        queryClient.setQueryData<AppConfiguration>(
          ['app-config', memberUuid],
          (prev) => applyMetaAdRename(prev, adId, nextName),
        );
      }
      toast.success(response.message);
      onSaved();
    } catch (err) {
      const msg = formatSaveError(err);
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    void performSave();
  };

  // ── Render ─────────────────────────────────────────────────────────
  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      );
    }
    if (loadError || !ad) {
      return (
        <div className="py-6">
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {loadError ?? 'Failed to load ad.'}
          </div>
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Effective-status warning — only for actionable Meta review
             states. IN_PROCESS / PENDING_REVIEW / PREAPPROVED are
             transient (Meta processes every fresh edit through them),
             so surfacing them adds noise. The three below actually
             require the user to do something. */}
        {ad.effective_status && (() => {
          const es = ad.effective_status.toUpperCase();
          const actionable: Record<string, { label: string; message: string }> = {
            DISAPPROVED: {
              label: 'Disapproved',
              message:
                "Meta rejected this ad — it won't deliver until you edit the creative to fix the policy issue. Check Meta Ads Manager for the specific policy violation.",
            },
            WITH_ISSUES: {
              label: 'Has issues',
              message:
                "Meta flagged an issue with this ad. Delivery may be limited — check Meta Ads Manager for details.",
            },
            PENDING_BILLING_INFO: {
              label: 'Pending billing info',
              message:
                "Meta is holding this ad because the ad account's billing information is incomplete. Add a payment method in Meta Ads Manager to enable delivery.",
            },
          };
          const state = actionable[es];
          if (!state) return null;
          return (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-900 text-sm">
              <p className="font-medium">{state.label}</p>
              <p className="text-xs mt-1">{state.message}</p>
            </div>
          );
        })()}

        {/* Ad name — inline counter layout matches Google's RSAEditor
             pattern (input flex-1 + fixed-width counter to the right). */}
        <div>
          <label
            htmlFor="meta-ad-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Ad name{' '}
            <span className="text-red-500" aria-label="required">*</span>
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="meta-ad-name"
              type="text"
              value={adName}
              onChange={(e) => setAdName(e.target.value)}
              placeholder="e.g. Spring sale — main image"
              maxLength={AD_NAME_MAX + 20}
              required
              className="flex-1"
            />
            <span
              className={
                (adName.length > AD_NAME_MAX
                  ? 'text-xs text-red-600 font-medium'
                  : 'text-xs text-gray-500') +
                ' w-16 flex-shrink-0'
              }
            >
              {adName.length}/{AD_NAME_MAX}
            </span>
          </div>
        </div>

        {/* Creative body */}
        {ad.creative_kind === 'image' && linkSpec ? (
          <div className="border-t border-gray-200 pt-6">
            <LinkCreativeStep
              value={linkSpec}
              onChange={setLinkSpec as React.Dispatch<React.SetStateAction<MetaLinkCreativeSpec>>}
              pendingImage={pendingImage}
              onChangePendingImage={setPendingImage}
              advantagePlusCreative={advantagePlusCreative}
              onChangeAdvantagePlusCreative={setAdvantagePlusCreative}
              // Lead-form picker: enabled only when the parent campaign
              // objective is OUTCOME_LEADS. `parentDestinationType` is
              // not surfaced on GetMetaAdResponse — we don't know it
              // without another Meta call, so we pass `null` and let
              // LinkCreativeStep show its "non-ON_AD info line" only
              // when relevant. When we skip the LEADS branch, no picker
              // renders regardless.
              isLeadsFormPickerApplicable={
                ad.campaign_objective === 'OUTCOME_LEADS'
              }
              parentDestinationType={null}
              pageId={ad.page_id ?? undefined}
              adAccountId={adAccountId}
              existingImageUrl={linkSpec.image_url}
            />
          </div>
        ) : (
          <div className="border-t border-gray-200 pt-6">
            <ReadOnlyCreativePreview ad={ad} />
          </div>
        )}

        {saveError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {saveError}
          </div>
        )}
      </div>
    );
  };

  // Inline card — parity with Google's RSAEditor / RDAEditor which
  // render inline via `AssetManagement/index.tsx:222`. The parent
  // (`MetaManagementAccordion`) early-returns this component in place
  // of the ad-set list when `editingAd` is set, so the editor takes
  // over the tab's content area rather than covering the page.
  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow border max-w-4xl w-full">
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="min-w-0 flex-1">
            <h2 id="meta-ad-editor-title" className="text-xl font-semibold text-gray-900">
              Manage Ad Assets
            </h2>
            {/* Persistent parent-entity label — mirrors RDAEditor's
                 "RDA in <ad group>" subtitle. Always rendered with
                 "Ad Set" fallback so the label stays visible even when
                 ad_set_name is null on the source data. */}
            <p className="text-sm text-gray-500">
              Ad in{' '}
              <span className="font-semibold text-gray-700">
                {adSetName || 'Ad Set'}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            aria-label="Close editor"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {renderBody()}

        {!loading && !loadError && ad && (
          <div className="flex justify-end gap-2 pt-6 mt-6 border-t">
            <Button onClick={onClose} variant="secondary" disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveClick}
              variant="primary"
              disabled={saving || !anyDirty}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Read-only preview for video / carousel / unknown creatives ────────────

interface ReadOnlyCreativePreviewProps {
  ad: GetMetaAdResponse;
}

function ReadOnlyCreativePreview({ ad }: ReadOnlyCreativePreviewProps) {
  const kindLabel =
    ad.creative_kind === 'video'
      ? 'video'
      : ad.creative_kind === 'carousel'
      ? 'carousel'
      : 'unrecognized';

  // Best-effort projection of a few fields for display. Full editing
  // isn't wired for these types today — Meta doesn't have a clean
  // API-side model for "swap the video in an ad" without rebuilding
  // the whole creative, and the wizard doesn't yet author video /
  // carousel, so the editor doesn't either.
  const oss = (ad.raw_object_story_spec ?? {}) as Record<string, unknown>;
  const linkData = (oss.link_data as Record<string, unknown> | undefined) ?? {};
  const videoData = (oss.video_data as Record<string, unknown> | undefined) ?? {};
  const preview = {
    headline: (linkData.name as string) ?? (videoData.title as string) ?? null,
    message: (linkData.message as string) ?? (videoData.message as string) ?? null,
    link: (linkData.link as string) ?? null,
    childCount: Array.isArray(linkData.child_attachments)
      ? (linkData.child_attachments as unknown[]).length
      : null,
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-900 text-sm">
        <p className="font-medium">
          This is a {kindLabel} ad — creative editing isn't yet supported in Flouri.
        </p>
        <p className="text-xs mt-1">
          Ad name changes above still save through Flouri. Status
          changes for this ad live on the Campaign Structure card, one
          level up.
          To edit the creative itself (copy, image, video, or carousel
          cards), open this ad in{' '}
          <a
            href="https://business.facebook.com/adsmanager"
            target="_blank"
            rel="noreferrer"
            className="underline hover:no-underline font-medium"
          >
            Meta Ads Manager
          </a>
          .
        </p>
      </div>

      {ad.thumbnail_url && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Preview
          </p>
          <img
            src={ad.thumbnail_url}
            alt={ad.creative_name ?? 'Ad creative preview'}
            className="max-w-xs rounded border border-gray-200"
          />
        </div>
      )}

      <dl className="text-sm space-y-2">
        {preview.headline && (
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Headline</dt>
            <dd className="text-gray-900">{preview.headline}</dd>
          </div>
        )}
        {preview.message && (
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Primary text</dt>
            <dd className="text-gray-900 whitespace-pre-wrap">{preview.message}</dd>
          </div>
        )}
        {preview.link && (
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Destination</dt>
            <dd className="text-gray-900 break-all">
              <a
                href={preview.link}
                target="_blank"
                rel="noreferrer"
                className="underline hover:no-underline"
              >
                {preview.link}
              </a>
            </dd>
          </div>
        )}
        {preview.childCount !== null && preview.childCount > 0 && (
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Carousel cards</dt>
            <dd className="text-gray-900">{preview.childCount}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

export default MetaAdEditor;
